import { useEffect, useState } from "react";
import { GeoCell } from "@/components/GeoCell";
import { AllowedCountries } from "@/components/AllowedCountries";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export const UserSecurityDialog = ({ user, onClose }) => {
  const [d, setD] = useState(null);
  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/users/${user.id}/security`, { headers: getAuthHeader() });
      setD(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load history"); }
  };
  // eslint-disable-next-line
  useEffect(() => { if (user) { setD(null); load(); } }, [user]);
  if (!user) return null;
  const act = async (path, msg) => {
    try { await axios.post(`${API}/admin/users/${user.id}/${path}`, path === "ips/verify" ? msg : {}, { headers: getAuthHeader() }); toast.success("Done"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const locked = d?.locked_until && new Date(d.locked_until) > new Date();
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="user-security-dialog">
        <DialogHeader>
          <DialogTitle>Security — {user.name} <span className="text-sm text-slate-400 font-normal">{user.email}</span></DialogTitle>
          <DialogDescription>Known IPs, login attempts and sessions</DialogDescription>
        </DialogHeader>
        {d && (
          <div className="space-y-4 max-h-[65vh] overflow-auto text-xs">
            {locked && (
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded p-2" data-testid="user-security-locked">
                <span className="text-red-700">Login locked until {fmt(d.locked_until)} (too many wrong passwords)</span>
                <Button size="sm" variant="outline" className="h-6" onClick={() => act("unlock-login")} data-testid="user-security-unlock">Unlock now</Button>
              </div>
            )}
            <AllowedCountries userId={user.id} initial={d.allowed_countries || []} />
            <section>
              <h4 className="font-semibold text-slate-700 mb-1">Known IP addresses</h4>
              <div className="flex flex-wrap gap-1.5">
                {d.known_ips.map((k) => (
                  <span key={k.ip} className={`px-2 py-0.5 rounded-full border font-mono ${k.verified ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}
                    data-testid={`user-ip-${k.ip}`}>
                    {k.ip} {k.geo?.label && <span className="font-sans text-[10px] text-slate-500">· {k.geo.label}</span>} {k.verified ? "✓" : (
                      <button className="ml-1 underline font-sans" onClick={() => act("ips/verify", { ip: k.ip })} data-testid={`user-ip-verify-${k.ip}`}>verify</button>
                    )}
                  </span>
                ))}
                {!d.known_ips.length && <span className="text-slate-400">No logins recorded yet</span>}
              </div>
            </section>
            <section>
              <h4 className="font-semibold text-slate-700 mb-1">Login history (last 100)</h4>
              <table className="w-full"><tbody>
                {d.events.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="p-1 whitespace-nowrap">{fmt(e.at)}</td>
                    <td className="p-1 font-mono">{e.ip}</td>
                    <td className="p-1 pr-3"><GeoCell geo={e.geo} ip={e.ip} compact /></td>
                    <td className="p-1">{e.success ? <span className="text-emerald-700 font-semibold">success</span> : <span className="text-red-600 font-semibold">failed</span>}</td>
                    <td className="p-1 text-amber-700 max-w-[220px] truncate" title={e.reason}>{(e.reason || "").replace(/^blocked country: .*,\s*([^,]+)$/, "blocked country: $1")}</td>
                    <td className="p-1 text-slate-400 truncate max-w-[260px]">{e.user_agent}</td>
                  </tr>
                ))}
              </tbody></table>
            </section>
            <section>
              <h4 className="font-semibold text-slate-700 mb-1">Sessions (last 50)</h4>
              <table className="w-full"><tbody>
                {d.sessions.map((s) => (
                  <tr key={s.sid} className="border-t">
                    <td className="p-1 whitespace-nowrap">{fmt(s.started_at)}</td>
                    <td className="p-1 whitespace-nowrap">→ {fmt(s.ended_at || s.last_seen)}</td>
                    <td className="p-1 font-mono">{s.ip}</td>
                    <td className="p-1"><GeoCell geo={s.geo} ip={s.ip} compact /></td>
                    <td className="p-1 text-slate-500">{s.end_reason || (s.ended_at ? "" : "open")}</td>
                  </tr>
                ))}
              </tbody></table>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
